/**
 * Deal Contact Panel Component
 * 거래 확정 화면 - PIN, 연락 정보 표시
 */

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Copy, Loader2, Phone, Video } from "lucide-react";
import { useState } from "react";

interface DealContactPanelProps {
  offerId: number;
  confirmPin?: string;
  onComplete?: () => void;
}

interface ContactBundle {
  offer_id: number;
  dm_thread_id: string;
  phone?: string;
  kakao_voice_link?: string;
  allow_phone: boolean;
  allow_voice_link: boolean;
}

export function DealContactPanel({
  offerId,
  confirmPin,
  onComplete,
}: DealContactPanelProps) {
  const { toast } = useToast();
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);

  // Fetch contact bundle
  const { data: contactBundle, isLoading } = useQuery<ContactBundle>({
    queryKey: ["/api/market/offers", offerId, "contact-bundle"],
    queryFn: async () => {
      const res = await fetch(`/api/market/offers/${offerId}/contact-bundle`);
      if (!res.ok) throw new Error("Failed to fetch contact bundle");
      return res.json();
    },
    enabled: !!offerId,
  });

  // Complete offer
  const completeMutation = useMutation({
    mutationFn: async (pin: string) => {
      return apiRequest("POST", `/api/market/offers/${offerId}/complete`, {
        pin,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market/offers"],
      });
      toast({
        title: "거래 완료",
        description: "거래가 성공적으로 완료되었습니다",
      });
      onComplete?.();
    },
    onError: (error: Error) => {
      toast({
        title: "완료 실패",
        description: error.message || "PIN이 올바르지 않습니다",
        variant: "destructive",
      });
    },
  });

  const handleCopyPin = () => {
    if (confirmPin) {
      navigator.clipboard.writeText(confirmPin);
      toast({
        title: "PIN 복사됨",
        description: "클립보드에 복사되었습니다",
      });
    }
  };

  const handleCopyPhone = () => {
    if (contactBundle?.phone) {
      navigator.clipboard.writeText(contactBundle.phone);
      toast({
        title: "전화번호 복사됨",
        description: "클립보드에 복사되었습니다",
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>거래 확정</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* PIN 표시 */}
        {confirmPin && (
          <div className="space-y-3">
            <Label>현장 인증 PIN</Label>
            <div className="flex items-center gap-2">
              <Input
                type={showPin ? "text" : "password"}
                value={confirmPin}
                readOnly
                className="font-mono text-lg tracking-wider"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => setShowPin(!showPin)}
              >
                {showPin ? "숨기기" : "보기"}
              </Button>
              <Button variant="outline" size="icon" onClick={handleCopyPin}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              현장에서 만날 때 이 PIN을 입력하세요
            </p>
          </div>
        )}

        <Separator />

        {/* 연락 정보 */}
        {contactBundle && (
          <div className="space-y-4">
            <Label>연락 정보</Label>

            {/* DM 스레드 */}
            {contactBundle.dm_thread_id && (
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-1">메시지</p>
                <p className="text-xs text-muted-foreground">
                  DM 스레드 ID: {contactBundle.dm_thread_id}
                </p>
              </div>
            )}

            {/* 전화번호 */}
            {contactBundle.allow_phone && contactBundle.phone && (
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{contactBundle.phone}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.location.href = `tel:${contactBundle.phone}`;
                    }}
                  >
                    전화하기
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyPhone}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* 카카오 음성통화 */}
            {contactBundle.allow_voice_link &&
              contactBundle.kakao_voice_link && (
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Video className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">카카오톡 음성통화</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      window.location.href = contactBundle.kakao_voice_link!;
                    }}
                  >
                    통화하기
                  </Button>
                </div>
              )}

            {!contactBundle.allow_phone && !contactBundle.allow_voice_link && (
              <p className="text-sm text-muted-foreground">
                판매자가 연락 정보 공개를 허용하지 않았습니다. DM을 통해
                연락하세요.
              </p>
            )}
          </div>
        )}

        <Separator />

        {/* 거래 완료 (PIN 입력) */}
        {confirmPin && (
          <div className="space-y-3">
            <Label htmlFor="pin-input">거래 완료 PIN 입력</Label>
            <div className="flex items-center gap-2">
              <Input
                id="pin-input"
                type="text"
                placeholder="6자리 PIN 입력"
                value={pin}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setPin(value);
                }}
                maxLength={6}
                className="font-mono text-lg tracking-wider text-center"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => {
                if (pin.length === 6) {
                  completeMutation.mutate(pin);
                } else {
                  toast({
                    title: "PIN 입력",
                    description: "6자리 PIN을 입력해주세요",
                    variant: "destructive",
                  });
                }
              }}
              disabled={completeMutation.isPending || pin.length !== 6}
            >
              {completeMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  처리 중...
                </>
              ) : (
                "거래 완료"
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
